// @ts-nocheck
/**
 * Arcane on-chain program helpers.
 *
 * Provides PDA derivation, deposit/withdraw transaction builders,
 * and indexer API calls that the UI components consume.
 */

import {ComputeBudgetProgram, PublicKey, Transaction, TransactionMessage, VersionedTransaction, Keypair} from '@solana/web3.js';
import {Buffer} from 'buffer';
import {
  getIndexerUrl,
  getNetwork,
  getProgramIdString,
  getTokenMints,
  getDepositLookupTableAddress,
  getDepositQueueAddress,
  resolveAllowedTokenFromMint,
} from './config/index.js';
import {fetchRelayersForNetwork} from './config/relayers.js';
import {getConfig} from './config/runtime.js';
import {appendIndexerAsset} from './tokenAssets.js';
import {parseSecretNote, to32ByteBuffer} from './zkProof.impl.js';
import nacl from 'tweetnacl';
import {getOracleQuote} from './rangeSDK.js';
import * as anchor from "@coral-xyz/anchor";
import { getAssociatedTokenAddressSync, NATIVE_MINT, TOKEN_2022_PROGRAM_ID, TOKEN_PROGRAM_ID } from '@solana/spl-token';

let _programId = null;
let _programIdString = null;

export function getProgramId() {
  const idString = getProgramIdString();
  if (!_programId || _programIdString !== idString) {
    _programId = new PublicKey(idString);
    _programIdString = idString;
  }
  return _programId;
}

const CONFIG_SEED = Buffer.from('arcane-config');

// ── PDA Derivation ───────────────────────────────────────────────────

export function getPoolPDA(denominationLamports) {
  const buf = Buffer.alloc(8);
  buf.writeBigUInt64BE(BigInt(denominationLamports));
  return PublicKey.findProgramAddressSync(
    [Buffer.from('pool'), buf.slice(0, 4), buf.slice(4, 8)],
    getProgramId()
  );
}

export function getTokenPoolPDA(mint, denominationSmallestUnits) {
  const mintKey = mint instanceof PublicKey ? mint : new PublicKey(mint);
  const buf = Buffer.alloc(8);
  buf.writeBigUInt64BE(BigInt(denominationSmallestUnits));
  return PublicKey.findProgramAddressSync(
    [Buffer.from('pool'), mintKey.toBuffer(), buf],
    getProgramId()
  );
}

export function getTreasuryPDA() {
  return PublicKey.findProgramAddressSync([Buffer.from('treasury')], getProgramId());
}

export function getCommitmentPDA(commitmentBigInt) {
  const bytes = to32ByteBuffer(BigInt(commitmentBigInt));
  return PublicKey.findProgramAddressSync(
    [Buffer.from('commitment'), bytes],
    getProgramId()
  );
}

export function getNullifierPDA(nullifierHashBigInt) {
  const bytes = to32ByteBuffer(BigInt(nullifierHashBigInt));
  return PublicKey.findProgramAddressSync(
    [Buffer.from('nullifier'), bytes],
    getProgramId()
  );
}

export function getNetworkStatePDA() {
  return PublicKey.findProgramAddressSync([CONFIG_SEED], getProgramId());
}

// ── Deposit Transaction Builder ──────────────────────────────────────
async function buildDepositIx(senderPubkey, commitmentBytes, denominationLamports, mint = null, tempSigner = undefined) {
  const { program, connection } = getConfig();
  const isNativeDeposit = mint === NATIVE_MINT.toBase58()
  
  const [poolPDA] = getPoolPDA(denominationLamports);
  const [treasuryPDA] = getTreasuryPDA();

  const commitmentArray = Array.from(commitmentBytes);
  const commitmentBuf = Buffer.from(commitmentArray);
  const [commitmentPDA] = PublicKey.findProgramAddressSync(
    [Buffer.from('commitment'), commitmentBuf],
    getProgramId()
  );
  const queueAccount = new PublicKey(getDepositQueueAddress());

  const remainingAccounts = []
  if (tempSigner) {
    remainingAccounts.push({
      pubkey: tempSigner instanceof PublicKey ? tempSigner : new PublicKey(tempSigner),
      isWritable: false,
      isSigner: true,
    })
  }
  let depositInx;
  if (isNativeDeposit) {
    depositInx = await program.methods
    .deposit(Array.from(commitmentBytes))
    .accountsPartial({
      sender: senderPubkey,
      pool: poolPDA,
      treasury: treasuryPDA,
      commitmentAccount: commitmentPDA,
      queue: queueAccount,
      slotHashes: anchor.web3.SYSVAR_SLOT_HASHES_PUBKEY,
      instructions: anchor.web3.SYSVAR_INSTRUCTIONS_PUBKEY,
      systemProgram: anchor.web3.SystemProgram.programId,
    })
    // .preInstructions([sigVerifyIx])
    .remainingAccounts(remainingAccounts)
    .instruction()
  } else {
    const mintPubkey = new PublicKey(mint);
    let tokenProgramForMint = TOKEN_PROGRAM_ID;

    const mintAccountInfo = await connection.getAccountInfo(mintPubkey);
    if (!mintAccountInfo) {
      throw new Error(`Mint account not found: ${mintPubkey.toBase58()}`);
    }
    if (mintAccountInfo.owner.equals(TOKEN_PROGRAM_ID)) {
      tokenProgramForMint = TOKEN_PROGRAM_ID;
    } else if (mintAccountInfo.owner.equals(TOKEN_2022_PROGRAM_ID)) {
      tokenProgramForMint = TOKEN_2022_PROGRAM_ID;
    } else {
      throw new Error(
        `Unsupported mint owner for ${mintPubkey.toBase58()}: ${mintAccountInfo.owner.toBase58()}`
      );
    }

    const [tokenPoolPDA] = getTokenPoolPDA(mintPubkey, denominationLamports);

    depositInx = await program.methods
    .tokenDeposit(Array.from(commitmentBytes))
    .accountsPartial({
      sender: senderPubkey,
      senderAta: getAssociatedTokenAddressSync(mintPubkey, senderPubkey, false, tokenProgramForMint),
      pool: tokenPoolPDA,
      treasury: treasuryPDA,
      treasuryAta: getAssociatedTokenAddressSync(mintPubkey, treasuryPDA, true, tokenProgramForMint),
      commitmentAccount: commitmentPDA,
      queue: queueAccount,
      slotHashes: anchor.web3.SYSVAR_SLOT_HASHES_PUBKEY,
      instructions: anchor.web3.SYSVAR_INSTRUCTIONS_PUBKEY,
      mint: mintPubkey,
      tokenProgram: tokenProgramForMint,
      systemProgram: anchor.web3.SystemProgram.programId,
    })
    .remainingAccounts(remainingAccounts)
    .instruction()
  }

  // depositTx.feePayer = senderPubkey;
  // depositTx.recentBlockhash = (await connection.getLatestBlockhash()).blockhash;

  // depositTx.add(
    
  // );

  return depositInx;
}

/**
 * Builds a deposit transaction for a pre-generated secret note.
 *
 * @param {object} params
 * @param {import('@solana/web3.js').PublicKey | string} params.senderPublicKey
 * @param {string} params.note
 * @param {boolean} [params.onChainBackup=false]
 * @param {number} [params.computeUnitPrice=5000]
 * @returns {Promise<{ tx: Transaction, note: string }>}
 */
export async function buildDepositTx({
  senderPublicKey,
  note,
  onChainBackup = false,
  computeUnitPrice = 5000,
  tempSigner = undefined,
}) {
  const { program, connection } = getConfig();
  const sender =
    senderPublicKey instanceof PublicKey
      ? senderPublicKey
      : new PublicKey(senderPublicKey);
  const parsed = parseSecretNote(note);
  const tokenMints = getTokenMints();
  const mintInput = parsed.mint ?? tokenMints.Solana;
  const {symbol, mint} = resolveAllowedTokenFromMint(mintInput);
  
  const ixs = []
  const commitmentBytes = to32ByteBuffer(BigInt(parsed.commitment));
  const lamports = BigInt(parsed.amount);
  const depositMint = symbol === 'Solana' ? tokenMints.Solana : mint;
  const depositInx = await buildDepositIx(
    sender,
    commitmentBytes,
    lamports,
    depositMint,
    tempSigner
  );
  ixs.push(depositInx)
  
  ixs.push(
    ComputeBudgetProgram.setComputeUnitPrice({
      microLamports: computeUnitPrice,
    })
  );

  const lookupTableAddress = getDepositLookupTableAddress();
  if (!lookupTableAddress) {
    throw new Error(
      `arcane-privacy-sdk: deposit lookup table is not configured for ${getNetwork()}`
    );
  }
  const lookupTableAccount = await connection.getAddressLookupTable(
    new PublicKey(lookupTableAddress)
  );
  if (!lookupTableAccount.value) {
    throw new Error(`arcane-privacy-sdk: deposit lookup table not found: ${lookupTableAddress}`);
  }

  if (onChainBackup) {
    const {encryptKey} = getConfig();
    if (!encryptKey) {
      throw new Error(
        'arcane-privacy-sdk: encryptKey is not set. Call getEncryptionKey() before buildDepositTx with onChainBackup enabled.'
      );
    }

    const encryptedNote = encryptData(encryptKey, note);

    const [notesV2PDA] = PublicKey.findProgramAddressSync(
      [Buffer.from('notes_v2'), sender.toBuffer()],
      program.programId
    );
    const notesV2Account = await program.account.notesV2.fetchNullable(notesV2PDA);

    if (!notesV2Account) {
      ixs.push(
        await program.methods
          .initNotes()
          .accountsPartial({
            user: sender,
          })
          .instruction()
      );
    } else if (notesV2Account && Math.ceil(notesV2Account.entries.length / 100) < Math.ceil((notesV2Account.entries.length + 1) / 100)){
      ixs.push(buildRequestHeapIx())
    }

    ixs.push(
      await program.methods
        .backupNotes([encryptedNote])
        .accountsPartial({
          user: sender,
        })
        .instruction()
    );
  }
  const {sigVerifyIx} = await getOracleQuote(sender, sender.toBase58());
  ixs.unshift(sigVerifyIx);
  
  const {blockhash} = await connection.getLatestBlockhash();
  const depositMessage = new TransactionMessage({
    payerKey: sender,
    recentBlockhash: blockhash,
    instructions: [...ixs],
  }).compileToV0Message([lookupTableAccount.value]);

  return new VersionedTransaction(depositMessage);
}

export async function buildDepositInstructionsForNotes(sender, notes) {
  const tokenMints = getTokenMints();

  return Promise.all(
    notes.map(async (note) => {
      const parsed = parseSecretNote(note);
      const mintInput = parsed.mint ?? tokenMints.Solana;
      const {symbol, mint} = resolveAllowedTokenFromMint(mintInput);
      const commitmentBytes = to32ByteBuffer(BigInt(parsed.commitment));
      const lamports = BigInt(parsed.amount);
      const depositMint = symbol === 'Solana' ? tokenMints.Solana : mint;

      return buildDepositIx(
        sender,
        commitmentBytes,
        lamports,
        depositMint
      );
    })
  );
}

export async function buildInitNotesInstructionIfNeeded(sender) {
  const {program} = getConfig();
  const [notesV2PDA] = PublicKey.findProgramAddressSync(
    [Buffer.from('notes_v2'), sender.toBuffer()],
    program.programId
  );
  const notesV2Account = await program.account.notesV2.fetchNullable(notesV2PDA);

  if (!notesV2Account) {
    return program.methods
      .initNotes()
      .accountsPartial({
        user: sender,
      })
      .instruction();
  }

  return null;
}

async function resolveEncryptKey() {
  const config = getConfig();
  if (config.encryptKey) {
    return config.encryptKey;
  }

  const key = await getEncryptionKey(config.wallet);
  config.encryptKey = key;
  return key;
}

export async function buildBackupNotesInstruction(sender, notes) {
  const {program} = getConfig();
  const encryptKey = await resolveEncryptKey();

  const encryptedNotes = notes.map((note) => encryptData(encryptKey, note));

  return program.methods
    .backupNotes(encryptedNotes)
    .accountsPartial({
      user: sender,
    })
    .instruction();
}

export async function isExtendBackupPdaNeeded(walletPubkey, newNotesLength) {
  const {program} = getConfig();

  const [notesV2PDA] = PublicKey.findProgramAddressSync(
    [Buffer.from("notes_v2"), walletPubkey.toBuffer()],
    program.programId
  );

  const notesV2Account = await program.account.notesV2.fetchNullable(notesV2PDA);
  if (notesV2Account && Math.ceil(notesV2Account.entries.length / 100) < Math.ceil((notesV2Account.entries.length + newNotesLength) / 100)) return true
  else return false
}

export function buildRequestHeapIx() {
  return ComputeBudgetProgram.requestHeapFrame({
    bytes: 32 * 1024,
  });
}

export async function buildDepositVersionedTransaction(
  sender,
  instructions,
  computeUnitPrice = 5000
) {
  const {connection} = getConfig();
  const computeUnitIx = ComputeBudgetProgram.setComputeUnitPrice({
    microLamports: computeUnitPrice,
  });
  const lookupTableAddress = getDepositLookupTableAddress();
  if (!lookupTableAddress) {
    throw new Error(
      `arcane-privacy-sdk: deposit lookup table is not configured for ${getNetwork()}`
    );
  }

  const lookupTableAccount = await connection.getAddressLookupTable(
    new PublicKey(lookupTableAddress)
  );
  if (!lookupTableAccount.value) {
    throw new Error(`arcane-privacy-sdk: deposit lookup table not found: ${lookupTableAddress}`);
  }
  const addresses = lookupTableAccount.value.state.addresses.map((address) => address.toBase58())

  const {blockhash} = await connection.getLatestBlockhash();
  const depositMessage = new TransactionMessage({
    payerKey: sender,
    recentBlockhash: blockhash,
    instructions: [...instructions, computeUnitIx],
  }).compileToV0Message([lookupTableAccount.value]);

  return new VersionedTransaction(depositMessage);
}

async function buildBackupTransaction(sender, notes) {
  const {connection} = getConfig();
  const tx = new Transaction();
  tx.add(await buildBackupNotesInstruction(sender, notes));

  const {blockhash} = await connection.getLatestBlockhash();
  tx.recentBlockhash = blockhash;
  tx.feePayer = sender;

  return tx;
}

/**
 * Builds batched deposit transactions for multiple pre-generated secret notes.
 *
 * Returns a v0 deposit transaction (with address lookup table) containing
 * [sigVerifyIx, depositIx..., initNotesIx?, computeUnitIx] and an optional
 * legacy backup transaction containing a single backupNotesIx with all
 * encrypted notes passed as the instruction argument array.
 *
 * @param {object} params
 * @param {import('@solana/web3.js').PublicKey | string} params.senderPublicKey
 * @param {string[]} params.notes
 * @param {boolean} [params.onChainBackup=false]
 * @param {number} [params.computeUnitPrice=5000]
 * @returns {Promise<{ depositTx: import('@solana/web3.js').VersionedTransaction, backupTx?: import('@solana/web3.js').Transaction, notes: string[] }>}
 */
export async function buildBatchDepositTxs({
  senderPublicKey,
  notes,
  onChainBackup = false,
  computeUnitPrice = 5000,
}) {
  const sender =
    senderPublicKey instanceof PublicKey
      ? senderPublicKey
      : new PublicKey(senderPublicKey);

  if (!notes.length) {
    throw new Error('arcane-privacy-sdk: buildBatchDepositTxs requires at least one note');
  }

  const [{sigVerifyIx}, depositInstructions, initNotesIx] = await Promise.all([
    getOracleQuote(sender, sender.toBase58()),
    buildDepositInstructionsForNotes(sender, notes),
    buildInitNotesInstructionIfNeeded(sender),
  ]);
  const depositInstructionsList = [sigVerifyIx, ...depositInstructions];
  if (initNotesIx) {
    depositInstructionsList.push(initNotesIx);
  }

  const depositTx = await buildDepositVersionedTransaction(
    sender,
    depositInstructionsList,
    computeUnitPrice
  );

  const backupTx = onChainBackup
    ? await buildBackupTransaction(sender, notes)
    : undefined;

  return {depositTx, backupTx, notes};
}

export async function fetchPlatformFee() {
  const { program } = getConfig();
  const [networkStatePDA] = getNetworkStatePDA();
  const networkState = await program.account.networkState.fetch(
    networkStatePDA
  );

  let feeTmp = 0;
  networkState.config.wallets?.map((wallet) => {
    feeTmp += wallet.feeSplit;
  })
  return (feeTmp / 100).toString();
}

// ── Indexer API ──────────────────────────────────────────────────────

function indexerParams(base, indexerAsset = null) {
  const params = new URLSearchParams(base);
  appendIndexerAsset(params, indexerAsset);
  return params;
}

export async function fetchCommitments(denomination, indexerAsset = null) {
  const params = indexerParams({denomination: String(denomination)}, indexerAsset);
  const res = await fetch(`${getIndexerUrl()}/commitments?${params}`);
  if (!res.ok) throw new Error('Failed to fetch commitments');
  const {commitments} = await res.json();
  return commitments;
}

export async function validateNullifier(nullifierHash, indexerAsset = null) {
  const params = indexerParams({nullifierHash}, indexerAsset);
  const res = await fetch(`${getIndexerUrl()}/validate-nullifier?${params}`);
  if (res.status === 429) throw new Error('Indexer rate limited (429)');
  if (!res.ok) throw new Error('Failed to validate nullifier');
  return res.json();
}

export async function fetchTxStatus(commitmentHex, nullifierHashHex, indexerAsset = null) {
  const params = indexerParams({commitment: commitmentHex, nullifierHash: nullifierHashHex}, indexerAsset);
  const res = await fetch(`${getIndexerUrl()}/tx-status?${params}`, {
    method: 'GET',
    headers: {'Content-Type': 'application/json'},
  });
  if (res.status === 429) throw new Error('Indexer rate limited (429)');
  if (!res.ok) throw new Error('Failed to fetch tx status');
  return res.json();
}

export async function fetchDepositCountByCommitment(commitment, denomination, indexerAsset = null) {
  const params = indexerParams({
    commitment,
    denomination: String(denomination),
  }, indexerAsset);
  const res = await fetch(`${getIndexerUrl()}/deposit-count-by-commitment?${params}`);
  if (res.status === 404) return {depositCountAfterMine: 0};
  if (!res.ok) throw new Error('Failed to fetch deposit count');
  return res.json();
}

export async function fetchWalletBalance(wallet, {includeDeposits = false, indexerAsset = null} = {}) {
  const params = indexerParams({wallet}, indexerAsset);
  if (includeDeposits) params.set('includeDeposits', 'true');
  const res = await fetch(`${getIndexerUrl()}/wallet-balance?${params}`, {
    method: 'GET',
    headers: {'Content-Type': 'application/json'},
  });
  if (!res.ok) throw new Error('Failed to fetch wallet balance');
  return res.json();
}

export async function fetchRelayers(network) {
  const resolvedNetwork = network ?? getNetwork();
  return fetchRelayersForNetwork(resolvedNetwork, getIndexerUrl());
}

export async function fetchLatestTransactions(type = 'Deposit', indexerAsset = null) {
  const params = indexerParams({type}, indexerAsset);
  const res = await fetch(`${getIndexerUrl()}/latest-transactions?${params}`);
  if (!res.ok) throw new Error('Failed to fetch transactions');
  const {data} = await res.json();
  return data;
}

export async function fetchTxCounts(type = 'Deposit', indexerAsset = null) {
  const params = indexerParams({type}, indexerAsset);
  const res = await fetch(`${getIndexerUrl()}/total-tx-counts?${params}`);
  if (!res.ok) throw new Error('Failed to fetch counts');
  const {data} = await res.json();
  return data;
}

// ── Relayer API ──────────────────────────────────────────────────────

export function normalizePubkeyString(value) {
  if (value == null || value === '') {
    throw new Error('Missing public key');
  }
  if (value instanceof PublicKey) {
    return value.toBase58();
  }
  if (typeof value === 'string') {
    return new PublicKey(value.trim()).toBase58();
  }
  if (typeof value.toBase58 === 'function') {
    return value.toBase58();
  }
  return new PublicKey(value).toBase58();
}

export function buildRelayPayload({
  args,
  proofHex,
  denomination,
  withdrawAddress,
  relayerPubkey,
  mint = null,
}) {
  const recipient = normalizePubkeyString(withdrawAddress);
  const relayer = normalizePubkeyString(relayerPubkey);
  const payload = {
    args: [args[0], args[1], recipient, relayer, args[4], args[5]],
    proof: proofHex.startsWith('0x') ? proofHex : `0x${proofHex}`,
    denomination: String(denomination),
  };
  if (mint) {
    payload.mint = normalizePubkeyString(mint);
  }
  return payload;
}

export async function submitToRelayer(relayerUrl, payload) {
  const res = await fetch(`${relayerUrl}/relay`, {
    method: 'POST',
    headers: {'Content-Type': 'application/json'},
    body: JSON.stringify(payload),
  });

  if (!res.ok) {
    const err = await res.json().catch(() => ({}));
    throw new Error(err.error || 'Relay submission failed');
  }

  return res.json();
}

export async function pollRelayerJob(relayerUrl, jobId, maxWaitMs = 120_000) {
  const start = Date.now();

  while (Date.now() - start < maxWaitMs) {
    const res = await fetch(`${relayerUrl}/v1/jobs/${jobId}`);
    if (!res.ok) throw new Error('Failed to poll job status');

    const job = await res.json();

    if (job.status === 'CONFIRMED') return job;
    if (job.status === 'FAILED') throw new Error(job.errorMessage || 'Withdrawal failed');

    await new Promise(r => setTimeout(r, 3000));
  }

  throw new Error('Withdrawal timed out');
}

export async function getEncryptionKey(wallet) {
  const message = new TextEncoder().encode('arcane:notes:v1');

  let signature;
  if (wallet instanceof Keypair) {
    signature = nacl.sign.detached(message, wallet.secretKey);
  } else if (typeof wallet.signMessage === 'function') {
    signature = await wallet.signMessage(message);
  } else {
    throw new Error(
      'arcane-privacy-sdk: wallet must be a Keypair (backend) or provide signMessage (frontend wallet adapter)'
    );
  }

  const key = nacl.hash(signature);
  return key.slice(0, 32);
}

export function encryptData(key, plaintext) {
  const nonce = nacl.randomBytes(24);           // unique for each encryption
  const messageUint8 = new TextEncoder().encode(plaintext);

  const box = nacl.secretbox(messageUint8, nonce, key);

  // Combine nonce and ciphertext for storage
  const combined = new Uint8Array(nonce.length + box.length);
  combined.set(nonce);
  combined.set(box, nonce.length);

  return Buffer.from(combined);
}

export function decryptData(key, combined) {
  const nonce = combined.slice(0, 24);
  const ciphertext = combined.slice(24);

  const decrypted = nacl.secretbox.open(ciphertext, nonce, key);
  if (!decrypted) throw new Error('Decryption failed');

  return new TextDecoder().decode(decrypted);
}